import React, { memo } from "react";
import InputBox from "../Components/InputBox";
import SelectInput from "../Components/SelectInput";
import NoPhoto from "../../assets/blankphoto.webp";
import { MdCloudUpload } from "react-icons/md";
import {
  FaUser,
  FaBuilding,
  FaHome,
  FaPhone,
  FaEnvelope,
  FaCalendarAlt,
  FaUsers,
  FaFileContract,
} from "react-icons/fa";

const InfoCard = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-[#363062]" />
      <h3 className="text-sm font-medium text-[#363062]">{title}</h3>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const InfoItem = ({
  label,
  value,
  editValue,
  name,
  type = "text",
  onChange,
  editMode,
}) => (
  <div className="flex items-start justify-between group">
    <span className="text-xs text-gray-500">{label}</span>
    {editMode ? (
      <InputBox
        value={editValue}
        onChange={onChange}
        name={name}
        type={type}
        className="w-48 text-right bg-[#363062]/5 border-[#363062]/20 focus:border-[#363062] focus:ring-1 focus:ring-[#363062] transition-colors duration-200 px-3 py-1.5"
      />
    ) : (
      <span className="text-sm font-medium text-gray-900">{value}</span>
    )}
  </div>
);

const BasicInformationForm = memo(
  ({
    tenant,
    formData,
    editMode,
    handleChange,
    handleUpload,
    handleSearchChange,
    handleResidentSelect,
    handleSelectLeaseType,
    searchTerm,
    filteredResidents,
    leaseTypeOptions,
    getCapitalizedTerm,
    progress,
  }) => {
    return (
      <div>
        {/* Profile Photo and Upload */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm">
            <div className="flex flex-col items-center">
              <div className="relative mb-3">
                <img
                  src={tenant.primaryPhoto || NoPhoto}
                  className="w-32 h-32 rounded-full object-cover object-center border-2 border-[#363062]/10"
                  alt={`${formData.firstName} ${formData.lastName}`}
                />
                {editMode && (
                  <label
                    htmlFor="photo-upload"
                    className="absolute bottom-0 right-0 bg-[#8FCD00] text-white p-1.5 rounded-full cursor-pointer shadow-md hover:bg-[#8FCD00]/80 transition-colors"
                  >
                    <MdCloudUpload className="w-4 h-4" />
                  </label>
                )}
              </div>
              <h3 className="text-sm font-medium text-[#363062]">
                {formData.firstName} {formData.lastName}
              </h3>
              <p className="text-xs text-gray-500">{formData.email}</p>

              {editMode && (
                <div className="mt-3 w-full">
                  <input
                    type="file"
                    id="photo-upload"
                    className="hidden"
                    onChange={handleUpload}
                  />
                  {progress > 0 && (
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                      <div
                        className="bg-[#8FCD00] h-2.5 rounded-full"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Information Cards - 2 columns grid inside the 3 columns grid */}
          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Personal Information */}
            <InfoCard title="Personal Information" icon={FaUser}>
              <InfoItem
                label="First Name"
                value={tenant.firstName}
                editValue={formData.firstName}
                name="firstName"
                onChange={handleChange}
                editMode={editMode}
              />
              <InfoItem
                label="Last Name"
                value={tenant.lastName}
                editValue={formData.lastName}
                name="lastName"
                onChange={handleChange}
                editMode={editMode}
              />
              <InfoItem
                label="Address"
                value={tenant.address}
                editValue={formData.address}
                name="address"
                onChange={handleChange}
                editMode={editMode}
              />
            </InfoCard>

            {/* Contact Information */}
            <InfoCard title="Contact Information" icon={FaPhone}>
              <InfoItem
                label="Email"
                value={tenant.email}
                editValue={formData.email}
                name="email"
                type="email"
                onChange={handleChange}
                editMode={editMode}
              />
              <InfoItem
                label="Phone Number"
                value={tenant.phoneNumber}
                editValue={formData.phoneNumber}
                name="phoneNumber"
                type="tel"
                onChange={handleChange}
                editMode={editMode}
              />
            </InfoCard>

            {/* Lease Information */}
            <InfoCard title="Lease Information" icon={FaFileContract}>
              <InfoItem
                label="Lease Type"
                value={tenant.leaseType}
                editValue={formData.leaseType}
                name="leaseType"
                onChange={handleChange}
                editMode={editMode}
              />
              {editMode ? (
                <div className="flex items-start justify-between group">
                  <span className="text-xs text-gray-500">Lease Type</span>
                  <SelectInput
                    name="leaseType"
                    options={leaseTypeOptions}
                    onChange={handleSelectLeaseType}
                    value={leaseTypeOptions.find(
                      (option) =>
                        option.value?.toLowerCase() ===
                        formData.leaseType?.toLowerCase()
                    )}
                    className="w-48 text-right bg-[#363062]/5 border-[#363062]/20 focus:border-[#363062] focus:ring-1 focus:ring-[#363062] transition-colors duration-200"
                  />
                </div>
              ) : null}
              <InfoItem
                label="Lease Start"
                value={tenant.leaseStart}
                editValue={formData.leaseStart}
                name="leaseStart"
                type="date"
                onChange={handleChange}
                editMode={editMode}
              />
              <InfoItem
                label="Lease End"
                value={tenant.leaseEnd}
                editValue={formData.leaseEnd}
                name="leaseEnd"
                type="date"
                onChange={handleChange}
                editMode={editMode}
              />
            </InfoCard>

            {/* Residency Information */}
            <InfoCard title="Residency Information" icon={FaHome}>
              <div className="relative">
                <div className="flex items-start justify-between group">
                  <span className="text-xs text-gray-500">
                    {getCapitalizedTerm("identifier")}
                  </span>
                  {editMode ? (
                    <InputBox
                      value={formData.lot}
                      onChange={handleSearchChange}
                      name="lot"
                      className="w-48 text-right bg-[#363062]/5 border-[#363062]/20 focus:border-[#363062] focus:ring-1 focus:ring-[#363062] transition-colors duration-200 px-3 py-1.5"
                    />
                  ) : (
                    <span className="text-sm font-medium text-gray-900">
                      {tenant.lot}
                    </span>
                  )}
                </div>
                {editMode && searchTerm && filteredResidents.length > 0 && (
                  <ul className="bg-white p-2 rounded-md border border-gray-200 shadow-lg absolute z-20 right-0 mt-1 w-48 max-h-48 overflow-y-auto">
                    {filteredResidents.map((resident, index) => (
                      <li
                        key={index}
                        onClick={(e) => handleResidentSelect(e, resident)}
                        className="cursor-pointer hover:bg-[#363062]/10 px-2 py-1 text-xs rounded-sm"
                      >
                        {resident.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <InfoItem
                label="Number of Occupants"
                value={tenant.occupants}
                editValue={formData.occupants}
                name="occupants"
                type="number"
                onChange={handleChange}
                editMode={editMode}
              />
            </InfoCard>
          </div>
        </div>
      </div>
    );
  }
);

export default BasicInformationForm;
